import { useEffect } from 'react';
import { LazyQueryHookOptions } from '@apollo/client';
import { useLazyQuery } from '@apollo/client/react/hooks';
import { gql } from '@yi/core';

import { useStorage } from '~/src/common/StorageProvider';
import { isValidUserToken } from '~/src/common/user';

import { useCurrentUserSubscriptionCacheUpdate } from './CurrentUserSubscriptonCacheUpdateProvider';

export const useCurrentUserSubscription = ({
  skip = false,
  fetchPolicy = 'cache-and-network',
  ...restParams
}: LazyQueryHookOptions<
  gql.typesClient.Query_CurrentUserSubscription,
  gql.typesClient.Query_CurrentUserSubscriptionVariables
> & { skip?: boolean } = {}) => {
  const { onFetchedLatest, onFetchingLatest } = useCurrentUserSubscriptionCacheUpdate();
  const [getCurrentUserSubscription, { data, ...rest }] = useLazyQuery<
    gql.typesClient.Query_CurrentUserSubscription,
    gql.typesClient.Query_CurrentUserSubscriptionVariables
  >(gql.Query_CurrentUserSubscription, {
    ssr: false,
    fetchPolicy,
    ...restParams,
  });

  const storage = useStorage();

  useEffect(() => {
    if (isValidUserToken(storage.token) && !skip) {
      if (restParams.variables?.fetchLatest === true) {
        onFetchingLatest();
      }
      getCurrentUserSubscription().then(() => {
        if (restParams.variables?.fetchLatest === true) {
          onFetchedLatest();
        }
      });
    }
  }, [
    getCurrentUserSubscription,
    skip,
    storage.token,
    onFetchedLatest,
    onFetchingLatest,
    restParams.variables?.fetchLatest,
  ]);

  const subscription = data?.CurrentUserSubscription ? data.CurrentUserSubscription : null;

  return { subscription, ...rest };
};
